import { Link } from "@tanstack/react-router";
import { ArrowRight, CheckCircle2, Circle, X } from "lucide-react";
import { buildSetupChecklist, type SetupChecklistKey } from "@lucralink/shared";
import { useDismissChecklist, useSetupStatus } from "@/lib/hooks/useOnboarding";
import { cn } from "@/lib/utils";

const ITEM_COPY: Readonly<Record<SetupChecklistKey, { label: string; to: string; cta: string }>> = {
  channel: { label: "Conecte seu WhatsApp ou Telegram", to: "/app/settings/integracao", cta: "Conectar" },
  store: { label: "Cadastre uma loja de afiliado com credencial válida", to: "/app/account", cta: "Cadastrar" },
  destination: { label: "Escolha um destino para as ofertas", to: "/app/grupos", cta: "Escolher" },
  source: { label: "Monitore um grupo de origem", to: "/app/grupos", cta: "Monitorar" },
  firstLink: { label: "Gere seu primeiro link de afiliado", to: "/app/links", cta: "Gerar link" },
};

/** Checklist "falta pouco para a primeira promocao", calculado do estado real
 *  da conta (get_setup_status). Some sozinho quando completo; dispensavel. */
export function SetupChecklist({ dismissed }: { dismissed: boolean }): JSX.Element | null {
  const { data: status, isLoading, error } = useSetupStatus(!dismissed);
  const dismiss = useDismissChecklist();

  if (dismissed || isLoading || error || !status) return null;
  const checklist = buildSetupChecklist(status);
  if (checklist.isComplete) return null;
  const pct = Math.round((checklist.doneCount / checklist.total) * 100);

  return (
    <section aria-labelledby="setup-checklist-title" className="rounded-xl border border-primary/25 bg-primary/5 p-4 sm:p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <h2 id="setup-checklist-title" className="text-sm font-semibold text-foreground">
            Falta pouco para a sua primeira promoção
          </h2>
          <p className="text-xs text-muted-foreground">
            {checklist.doneCount} de {checklist.total} passos concluídos
          </p>
        </div>
        <button
          type="button"
          onClick={() => dismiss.mutate()}
          disabled={dismiss.isPending}
          aria-label="Dispensar checklist"
          className="rounded-md p-1.5 text-muted-foreground hover:bg-[hsl(var(--surface-raised))] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <X size={15} />
        </button>
      </div>
      <div
        className="mt-3 h-1.5 overflow-hidden rounded-full bg-border"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={checklist.total}
        aria-valuenow={checklist.doneCount}
        aria-label="Progresso da configuração"
      >
        <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${pct}%` }} />
      </div>
      <ul className="mt-4 space-y-1.5">
        {checklist.items.map((item) => {
          const copy = ITEM_COPY[item.key];
          return (
            <li key={item.key} className="flex flex-wrap items-center justify-between gap-2 rounded-lg px-2 py-1.5">
              <span className={cn("flex min-w-0 items-center gap-2 text-sm", item.done ? "text-muted-foreground line-through" : "text-foreground")}>
                {item.done ? (
                  <CheckCircle2 size={16} className="shrink-0 text-primary" aria-hidden="true" />
                ) : (
                  <Circle size={16} className="shrink-0 text-muted-foreground" aria-hidden="true" />
                )}
                {copy.label}
                <span className="sr-only">{item.done ? "(feito)" : "(pendente)"}</span>
              </span>
              {!item.done && (
                <Link
                  to={copy.to}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
                >
                  {copy.cta}
                  <ArrowRight size={12} aria-hidden="true" />
                </Link>
              )}
            </li>
          );
        })}
      </ul>
      {dismiss.isError && <p role="alert" className="mt-2 text-xs text-destructive">Não foi possível dispensar agora.</p>}
    </section>
  );
}
